import { requestNotificationPermission } from '@/lib/notification-utils';
import { playToastSound } from '@/lib/sound-utils';
import { generateContent } from '@/server/actions/sora/generate-actions';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

/**
 * Minimal shape of an active generation entry.
 * Each generator page has its own full ActiveGeneration interface in useGenerationQueue.
 * This hook only needs a subset for duplicate-checking and state updates.
 */
interface ActiveGenerationBase {
  taskId: string;
  prompt: string;
  imageUrl: string | null;
  phase: 'simulating' | 'polling';
  isPolling: boolean;
  progress: number;
  startTime: number;
  deferredError: string | null;
  knownFailed?: boolean;
  status?: string;
  failureReason?: string | null;
  aspectRatio?: string;
  mediaType?: 'video' | 'audio' | 'image';
  countdownDuration: number;
  loopCount: number;
  isPausing: boolean;
}

export interface UseSubmissionProps {
  isAuthenticated: boolean;
  contentInput: string;
  setContentInput: (val: string) => void;
  activeGenerations: Map<string, ActiveGenerationBase>;
  credits: number | null;
  setCredits: (val: number | null) => void;
  estimatedCost: number | null;
  subscriptionInfo: any;
  model: string;
  aspectRatio: string;
  resolution: string;
  duration: string;
  variations: string;
  mode: string;
  selectedImage: string | null;
  selectedImages: string[];
  setSelectedImage: (val: string | null) => void;
  setSelectedImages?: (val: string[]) => void;
  setIsSignupModalOpen: (val: boolean) => void;
  setIsPricingModalOpen: (val: boolean) => void;
  setIsTrialModalOpen: (val: boolean) => void;
  setIsGenerating: (val: boolean) => void;
  setLoadingTextIndex: (val: number) => void;
  setLoadingTextAutoplay: (val: boolean) => void;
  refreshCreditsAndSubscription: () => void;
  setActiveGenerations: (val: Map<string, ActiveGenerationBase>) => void;
  contentEditableRef: React.RefObject<HTMLTextAreaElement | null>;
  services: any[];
  autoSwitchEnabled: boolean;
  isFreePreviewEligible: boolean;
}

export function useSubmission({
  isAuthenticated,
  contentInput,
  setContentInput,
  activeGenerations,
  credits,
  setCredits,
  estimatedCost,
  subscriptionInfo,
  model,
  aspectRatio,
  resolution,
  duration,
  variations,
  mode,
  selectedImage,
  selectedImages,
  setSelectedImage,
  setSelectedImages,
  setIsSignupModalOpen,
  setIsPricingModalOpen,
  setIsTrialModalOpen,
  setIsGenerating,
  setLoadingTextIndex,
  setLoadingTextAutoplay,
  refreshCreditsAndSubscription,
  setActiveGenerations,
  contentEditableRef,
  services,
  autoSwitchEnabled,
  isFreePreviewEligible,
}: UseSubmissionProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [lastSubmittedPrompt, setLastSubmittedPrompt] = useState<string | null>(null);
  const [bypassDuplicateCheck, setBypassDuplicateCheck] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingGeneration, setPendingGeneration] = useState(false);

  // Handle successful payment redirect
  useEffect(() => {
    const success = searchParams.get('success');
    if (success === 'true') {
      const backup = localStorage.getItem('sora_prompt_backup');
      if (backup && !pendingGeneration) {
        setContentInput(backup);
        if (contentEditableRef.current) {
          contentEditableRef.current.value = backup;
        }
        setPendingGeneration(true);
      }
    }
  }, [searchParams, pendingGeneration, setContentInput, contentEditableRef]);

  const handleSubmit = async (
    upscaleTaskId?: string,
    upscalePrompt?: string,
    upscaleAspectRatio?: string
  ) => {
    return handleSubmitInternal(upscaleTaskId, upscalePrompt, upscaleAspectRatio);
  };

  // Placeholder for recursive calls
  const attemptAutoSwitchRef = useRef<any>(null);

  const handleSubmitInternal = async (
    taskIdParam?: string,
    promptParam?: string,
    aspectRatioParam?: string,
    options?: { overrideModel?: string; isAutoSwitch?: boolean }
  ) => {
    // ... move function body here ...
    if (!isAuthenticated) {
      setIsSignupModalOpen(true);
      return;
    }

    if (!taskIdParam && !contentInput.trim()) {
      playToastSound('info');
      toast.info('Please enter a description for your video');
      return;
    }

    // NOTE: Do NOT add a client-side credit guard here.
    // The server checks free preview eligibility and only returns 'Plans Ready!'
    // when the user genuinely has insufficient credits AND is not free-preview-eligible.
    // A client-side guard would bypass free preview routing for 0-credit new users.

    // Backup prompt in case of credit redirect / refresh
    const promptToSubmit = promptParam || contentInput;
    localStorage.setItem('sora_prompt_backup', promptToSubmit);

    // DUPLICATE CHECK
    const isJustSubmitted = promptToSubmit === lastSubmittedPrompt;

    let isProcessing = false;
    activeGenerations.forEach((gen) => {
      if (gen.prompt === promptToSubmit && !gen.knownFailed && !taskIdParam) {
        isProcessing = true;
      }
    });

    if (
      (isJustSubmitted || isProcessing) &&
      bypassDuplicateCheck !== promptToSubmit &&
      !taskIdParam &&
      !options?.isAutoSwitch
    ) {
      playToastSound('info');
      toast.warning('You have already requested this.', {
        description: 'Click Generate again if you really want to create another variation.',
        duration: 4000,
      });
      setBypassDuplicateCheck(promptToSubmit);
      return;
    }

    if (bypassDuplicateCheck !== promptToSubmit) {
      setBypassDuplicateCheck(null);
    }

    setIsSubmitting(true);
    toast.loading('Starting...', { id: 'submit-toast' });
    requestNotificationPermission();

    // NOTE: Do NOT open the pricing modal here. Let the server decide.
    // Opening it prematurely causes it to fire when credits are still loading (null) or
    // for any pay-as-you-go user even if they have sufficient credits.

    setIsGenerating(true);
    setLoadingTextIndex(0);
    setLoadingTextAutoplay(true);

    setLastSubmittedPrompt(promptToSubmit);
    setBypassDuplicateCheck(null);

    // Make sure we pass the correct preset handling for upscale
    const isUpscale = !!taskIdParam;
    const modelToUse = options?.overrideModel || (isUpscale ? 'grok-imagine-upscale' : model);

    // Explicit cost variable for rollback if API call fails
    const costToDeduct = isUpscale ? 10 : estimatedCost;

    // Optimistically update credits. Only deduct if the user has enough credits.
    // (This elegantly skips free-preview users or users who will get "Plans Ready!" anyway).
    if (credits !== null && costToDeduct !== null && credits >= costToDeduct) {
      const newBalance = credits - costToDeduct;
      setCredits(newBalance);
      window.dispatchEvent(
        new CustomEvent('credit-update', { detail: { optimisticBalance: newBalance } })
      );
    }

    // attemptAutoSwitch logic moved outside

    try {
      const result = await generateContent({
        prompt: promptToSubmit,
        mediaType:
          modelToUse === 'elevenlabs-sfx'
            ? 'audio'
            : modelToUse === 'nano-banana-2' || modelToUse === 'z-image'
              ? 'image'
              : 'video',
        preset: modelToUse,
        aspectRatio: isUpscale && aspectRatioParam ? aspectRatioParam : aspectRatio,
        resolution: isUpscale ? '720p' : resolution,
        duration: duration.replace('s', ''),
        variations: modelToUse === 'elevenlabs-sfx' ? '1' : variations,
        mode: modelToUse === 'grok-imagine' ? (mode as 'normal' | 'fun' | 'spicy') : undefined,
        imageUrl: selectedImage || undefined,
        imageUrls: selectedImages.length > 0 ? selectedImages : undefined,
        upscaleTaskId: taskIdParam,
        simulation: false,
        overrideServiceSlug: options?.isAutoSwitch ? modelToUse : undefined,
        autoSwitchEnabled: autoSwitchEnabled,
        isAutoSwitch: options?.isAutoSwitch,
      });

      if (result.success && result.data) {
        console.log('[DEBUG] Generation successful');
        // If this was a free preview (user is free-preview-eligible), open pricing modal alongside
        if (isFreePreviewEligible) {
          setIsPricingModalOpen(true);
        }
        if (!options?.isAutoSwitch) {
          playToastSound('success');
        }
        toast.success(
          options?.isAutoSwitch ? 'Auto-Switch successful! Generating...' : 'Generating...',
          { id: 'submit-toast' }
        );
        refreshCreditsAndSubscription();

        const newGens = new Map(activeGenerations);
        const returnedTasks = Array.isArray(result.data) ? result.data : [result.data];

        returnedTasks.forEach((gen: any) => {
          const genFailed = gen.status === 'failed' || gen.status === 'rejected';
          if (genFailed) {
            playToastSound('error');
            toast.error(gen.failureReason || 'Generation failed');
          } else {
            // Extracted countdownDuration from the API so it corresponds to the actual service resolved on the backend
            const countdownDuration = gen.countdownDuration || 150;

            newGens.set(gen.taskId, {
              taskId: gen.taskId,
              prompt: promptToSubmit,
              imageUrl: selectedImage,
              phase: 'simulating',
              isPolling: false,
              progress: 0,
              startTime: Date.now(),
              deferredError: null,
              knownFailed: false,
              status: gen.status,
              failureReason: null,
              aspectRatio: aspectRatio,
              mediaType:
                modelToUse === 'elevenlabs-sfx'
                  ? 'audio'
                  : modelToUse === 'nano-banana-2' || modelToUse === 'z-image'
                    ? 'image'
                    : 'video',
              countdownDuration,
              loopCount: 0,
              isPausing: false,
            });
          }
        });

        if (newGens.size > 0) {
          setActiveGenerations(newGens);
          setIsGenerating(true);
          setContentInput('');
          setSelectedImage(null);
          if (setSelectedImages) setSelectedImages([]);
          if (contentEditableRef.current) {
            contentEditableRef.current.value = '';
          }
        } else {
          setIsGenerating(false);
          refreshCreditsAndSubscription();
        }
      } else {
        if (
          result.message === 'Plans Ready!' ||
          result.message?.toLowerCase().includes('insufficient')
        ) {
          playToastSound('info');
          toast.success('Plans Ready!', { id: 'submit-toast' });
          setIsPricingModalOpen(true);
        } else {
          // Normal error handling
          playToastSound('error');
          toast.error(result.message || 'Generation failed', { id: 'submit-toast' });

          // If modal was previously opened, close it to avoid stuck state

          // Auto-Switch fallback ONLY for technical/retryable errors
          if (result.isRetryable) {
            console.log('[DEBUG] Error is retryable, attempting auto-switch');
            const handled = await attemptAutoSwitchRef.current?.(result.serviceSlug || modelToUse, {
              taskId: taskIdParam,
              prompt: promptToSubmit,
              aspectRatio: aspectRatio,
            });
            if (handled) return;
          } else {
            console.log('[DEBUG] Error is NOT retryable, skipping auto-switch', {
              message: result.message,
            });
          }
        }
        setIsGenerating(false);
        if (credits !== null && costToDeduct !== null) {
          setCredits(credits);
          window.dispatchEvent(
            new CustomEvent('credit-update', { detail: { optimisticBalance: credits } })
          );
        }
      }
    } catch (error) {
      console.error('Submit error:', error);
      playToastSound('error');
      toast.error('An unexpected error occurred', { id: 'submit-toast' });

      // If modal was previously opened, close it to avoid stuck state
      setIsPricingModalOpen(false);

      // Only attempt switch if it looks like a technical error
      const handled = await attemptAutoSwitchRef.current?.(modelToUse, {
        prompt: promptToSubmit,
        aspectRatio: aspectRatio,
      });
      if (handled) return;

      if (credits !== null && costToDeduct !== null) {
        setCredits(credits);
        window.dispatchEvent(
          new CustomEvent('credit-update', { detail: { optimisticBalance: credits } })
        );
      }
      setIsGenerating(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Universal Fallback Logic
   * Can be triggered on immediate submission failure OR later polling failure
   */
  const attemptAutoSwitch = useCallback(
    async (
      passedServiceSlug?: string,
      params?: { prompt?: string; aspectRatio?: string; taskId?: string }
    ) => {
      console.log('[DEBUG] attemptAutoSwitch called with:', {
        passedServiceSlug,
        autoSwitchEnabled,
        servicesCount: services.length,
      });
      if (!autoSwitchEnabled) return false;

      // Prioritize the exact slug from server if available
      let failedSlug = passedServiceSlug || '';

      if (!failedSlug) {
        // Find the service that just failed (Fall back to reconstruction if server didn't provide it)
        const alreadyIsSlug =
          model.includes('-720p-') ||
          model.includes('-1080p-') ||
          model.includes('-480p-') ||
          model === 'elevenlabs-sfx' ||
          model === 'z-image';

        failedSlug = alreadyIsSlug
          ? model
          : model === 'elevenlabs-sfx'
            ? 'elevenlabs-sfx'
            : model === 'nano-banana-2'
              ? `nano-banana-2-${resolution}`
              : model === 'z-image'
                ? 'z-image'
                : model === 'runway'
                  ? `runway-${resolution === '1080p' ? '1080p' : '720p'}-${duration.includes('10') ? '10s' : '5s'}`
                  : model === 'grok-imagine'
                    ? `grok-${resolution === '720p' ? '720p' : '480p'}-${duration.includes('15') ? '15s' : duration.includes('10') ? '10s' : '6s'}`
                    : `${model}-${resolution}-${duration === '5s' ? '10s' : duration}`;
      }

      const normalizedFailedSlug = failedSlug.trim().toLowerCase();
      let failedService = services.find(
        (s) => s.slug.trim().toLowerCase() === normalizedFailedSlug
      );
      if (!failedService) {
        failedService = services.find((s) => normalizedFailedSlug.includes(s.slug.toLowerCase()));
      }

      if (!failedService) {
        console.log('[DEBUG] Auto-Switch: Service not found', { normalizedFailedSlug });
        return false;
      }

      const fallbackId =
        failedService?.autoSwitchServiceId || (failedService as any)?.['auto-switch_service_id'];

      if (fallbackId) {
        const falloverService = services.find((s) => s.id === fallbackId);
        if (falloverService) {
          playToastSound('info');
          toast.success(`Auto-Switch: Re-routing to ${falloverService.name}...`, {
            id: 'submit-toast',
          });

          // Mark the current matching simulation as failed so it renders as an error card
          const newMap = new Map(activeGenerations);
          // Find by taskId explicitly or by prompt
          let mapKeyToFail: string | undefined;

          if (params?.taskId && newMap.has(params.taskId)) {
            mapKeyToFail = params.taskId;
          } else if (params?.prompt) {
            // Fallback finding by prompt
            for (const [key, gen] of newMap.entries()) {
              if (gen.prompt === params.prompt && !gen.knownFailed) {
                mapKeyToFail = key;
                break;
              }
            }
          }

          if (mapKeyToFail) {
            const existing = newMap.get(mapKeyToFail)!;
            newMap.set(mapKeyToFail, {
              ...existing,
              status: 'failed',
              knownFailed: true,
              failureReason: 'Generation Failed (Auto-Switching)',
              phase: 'polling', // Force drawing error UI instead of simulation
              progress: 100,
            });
            setActiveGenerations(newMap);
          }

          // Recursively call handleSubmit with the fallover model
          setTimeout(() => {
            handleSubmitInternal(undefined, params?.prompt, params?.aspectRatio, {
              overrideModel: falloverService.slug,
              isAutoSwitch: true,
            });
          }, 500);
          return true; // Handled
        }
      }
      return false; // Not handled
    },
    [autoSwitchEnabled, services, model, resolution, duration]
  );

  // Sync ref to allow handleSubmitInternal (which is not a hooks-dependency-aware callback yet)
  // to successfully call the latest version of attemptAutoSwitch
  useEffect(() => {
    attemptAutoSwitchRef.current = attemptAutoSwitch;
  }, [attemptAutoSwitch]);

  // Auto-resume generation after credit update
  useEffect(() => {
    if (
      pendingGeneration &&
      credits !== null &&
      estimatedCost !== null &&
      credits >= estimatedCost
    ) {
      setPendingGeneration(false);
      handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingGeneration, credits, estimatedCost]);

  // Handle Upscale Redirect URL Parameters
  const hasHandledUpscale = useRef(false);
  useEffect(() => {
    const upscaleTaskId = searchParams.get('upscale_task_id');
    const prompt = searchParams.get('prompt') || '';

    if (upscaleTaskId && !hasHandledUpscale.current && isAuthenticated && credits !== null) {
      if (credits < 10) {
        setIsPricingModalOpen(true);
      } else {
        const upscaleAspectRatio = searchParams.get('upscale_aspect_ratio') || undefined;

        // Strip the URL params silently so refreshing doesn't re-trigger it
        const newUrl = pathname;
        router.replace(newUrl, { scroll: false });
        hasHandledUpscale.current = true;

        // Slight timeout to ensure state is ready
        setTimeout(() => {
          handleSubmit(upscaleTaskId, prompt, upscaleAspectRatio);
        }, 100);
      }
    }
  }, [searchParams, isAuthenticated, credits, pathname, router]);

  return {
    isSubmitting,
    handleSubmit,
    pendingGeneration,
    attemptAutoSwitch,
  };
}
